import React from "react";

import pkg from "../../lib/pkg";
import { AnalyticsError } from "../../lib/util";
import { IntegrationCategory } from "../../providers/AnalyticsContext";
import useTrackEvent from "../useTrackEvent";

type MeetingTime = {
  dayOfWeek: number;
  hour: number;
  minute: number;
};

type Section = {
  uid: string;
  leader: {
    uid: string;
  };
  meetingTimes?: Array<MeetingTime>;
  progress: {
    currentWeek: number;
    pastMeetingCount: number;
    remainingMeetingCount: number;
    totalMeetingCount: number;
  } | null;
  start_time: string;
  end_time: string;
  duration_minutes: number | null;
  filledSpaceCount: number;
  size_min: number | null;
  size_max: number | null;
  allows_late_enrollment?: boolean;
  is_buyable?: boolean;
  is_in_progress?: boolean;
};

const EVENT_NAME = "Section List Viewed";

interface TrackSectionListViewedProperties {
  component: string;
  page: number;
  rowsPerPage: number;
  sections: Array<Section>;
  totalCount: number;
}

export default function useSectionListViewed() {
  const trackEvent = useTrackEvent(IntegrationCategory.Functional);
  const [tracked, setTracked] = React.useState<string>();

  function track(properties: TrackSectionListViewedProperties) {
    try {
      if (!properties?.sections?.length) {
        return;
      }

      const id = getTrackId(properties);

      if (tracked === id) {
        return;
      }

      const payload = mapPropertiesToPayload(properties);

      trackEvent(EVENT_NAME, payload);

      setTracked(id);
    } catch (error) {
      pkg.onError(new AnalyticsError(EVENT_NAME, error));
    }
  }

  return React.useCallback(track, [trackEvent, tracked]);
}

type SectionListViewedPayload = {
  component: string;
  pagination?: {
    current_page: number;
    rows_per_page: number;
    total_page_count: number;
    total_row_count: number;
  };
  sections?: Array<{
    [key: string]: unknown;
  }>;
  total_section_count: number;
  [key: string]: unknown;
};

function mapPropertiesToPayload(
  properties: TrackSectionListViewedProperties
): SectionListViewedPayload {
  const { component, page, rowsPerPage, sections, totalCount } = properties;
  const pagination = {
    current_page: page,
    rows_per_page: rowsPerPage,
    total_page_count: Math.ceil(totalCount / rowsPerPage),
    total_row_count: totalCount,
  };

  const schemaSections = sections.map((section, i) =>
    mapSectionToSchema(section, i)
  );

  return {
    component,
    pagination,
    sections: schemaSections,
    total_section_count: totalCount,
  };
}

function mapSectionToSchema(section: Section, index: number = 0) {
  const late_enrollment_offered =
    section.allows_late_enrollment && !!section.progress;
  const meeting_times =
    section.meetingTimes?.map(meetingTime =>
      mapMeetingTimeToSchema(meetingTime)
    ) || [];

  return {
    index,
    uid: section.uid,
    start_time: section.start_time,
    end_time: section.end_time,
    duration_minutes: section.duration_minutes,
    leader_uid: section.leader?.uid,
    filled_space_count: section.filledSpaceCount,
    size_min: section.size_min,
    size_max: section.size_max,
    allows_late_enrollment: section.allows_late_enrollment,
    is_buyable: section.is_buyable,
    is_in_progress: section.is_in_progress,
    total_meeting_count: section.progress?.totalMeetingCount,
    remaining_meeting_count: section.progress?.remainingMeetingCount,
    past_meeting_count: section.progress?.pastMeetingCount,
    current_week: section.progress?.currentWeek,
    meeting_times,
    late_enrollment_offered,
  };
}

function mapMeetingTimeToSchema(meetingTime: MeetingTime) {
  return {
    day_of_week: meetingTime.dayOfWeek,
    hour: meetingTime.hour,
    minute: meetingTime.minute,
  };
}

function getTrackId(properties: TrackSectionListViewedProperties): string {
  const ids = properties.sections.map(section => section.uid.substring(0, 8));

  return ids.join(":");
}
